import { useState } from "react";
import { useSiteSettings } from "../../../contexts/SiteSettingsContext";
import type { RankingItemData, RankingSectionData } from "../../../types/settings";
import ImageUploadField from "./ImageUploadField";

export default function RankingsTab() {
  const { settings, updateSettings } = useSiteSettings();
  const [activeSectionId, setActiveSectionId] = useState<string>(settings.rankingSections[0]?.id || "");

  const activeSection = settings.rankingSections.find((s) => s.id === activeSectionId);

  const updateSection = (id: string, patch: Partial<RankingSectionData>) => {
    updateSettings({
      rankingSections: settings.rankingSections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });
  };

  const updateItem = (sectionId: string, itemIdx: number, patch: Partial<RankingItemData>) => {
    updateSettings({
      rankingSections: settings.rankingSections.map((s) => {
        if (s.id !== sectionId) return s;
        const items = s.items.map((item, i) => (i === itemIdx ? { ...item, ...patch } : item));
        return { ...s, items };
      }),
    });
  };

  const removeItem = (sectionId: string, itemIdx: number) => {
    updateSettings({
      rankingSections: settings.rankingSections.map((s) => {
        if (s.id !== sectionId) return s;
        const items = s.items
          .filter((_, i) => i !== itemIdx)
          .map((item, i) => ({ ...item, rank: i + 1 }));
        return { ...s, items };
      }),
    });
  };

  const addItem = (sectionId: string) => {
    updateSettings({
      rankingSections: settings.rankingSections.map((s) => {
        if (s.id !== sectionId) return s;
        const newItem: RankingItemData = {
          rank: s.items.length + 1,
          href: "https://",
          img: "",
          title: "新しい記事タイトル",
          views: "0 views",
        };
        return { ...s, items: [...s.items, newItem] };
      }),
    });
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-700 mb-4 pb-2 border-b border-gray-200">ランキング編集</h3>

      {/* セクション選択タブ */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {settings.rankingSections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSectionId(s.id)}
            className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              activeSectionId === s.id ? "bg-white text-red-600 font-bold" : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {activeSection && (
        <div className="space-y-5">
          {/* セクション設定 */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">セクションタイトル</label>
              <input
                type="text"
                value={activeSection.title}
                onChange={(e) => updateSection(activeSectionId, { title: e.target.value })}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-red-400"
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">「もっと見る」URL</label>
                <input
                  type="url"
                  value={activeSection.moreHref}
                  onChange={(e) => updateSection(activeSectionId, { moreHref: e.target.value })}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">「もっと見る」ラベル</label>
                <input
                  type="text"
                  value={activeSection.moreLabel}
                  onChange={(e) => updateSection(activeSectionId, { moreLabel: e.target.value })}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
          </div>

          {/* アイテム一覧 */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-600">ランキング項目</p>
            {activeSection.items.map((item, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500">{i + 1}位</span>
                  <button
                    onClick={() => removeItem(activeSectionId, i)}
                    className="text-xs text-red-400 hover:text-red-600 cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line" /> 削除
                  </button>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">タイトル</label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateItem(activeSectionId, i, { title: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-red-400"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-0.5">URL</label>
                    <input
                      type="url"
                      value={item.href}
                      onChange={(e) => updateItem(activeSectionId, i, { href: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-red-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-0.5">ビュー数</label>
                    <input
                      type="text"
                      value={item.views}
                      onChange={(e) => updateItem(activeSectionId, i, { views: e.target.value })}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-red-400"
                      placeholder="1,234 views"
                    />
                  </div>
                </div>
                <div>
                  <ImageUploadField
                    label="サムネイル（URLまたはアップロード）"
                    value={item.img}
                    onChange={(url) => updateItem(activeSectionId, i, { img: url })}
                    placeholder="https://example.com/thumb.jpg"
                    previewClassName="h-16 w-full object-cover rounded-md"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => addItem(activeSectionId)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-red-400 rounded-lg text-sm text-red-600 hover:bg-red-50 cursor-pointer"
            >
              <i className="ri-add-line" /> 項目を追加
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
